// EstimationFilterModal.jsx
import React, { useState, useEffect } from "react";
import { Modal, Button, Col, Row } from "react-bootstrap";
import { ComboboxField } from "../../../../Components/formElements/ComboboxField";
import { InputField } from "../../../../Components/formElements/InputField";
import {
  mainBuildingTypes,
  buildingStrctures,
  clearSpanBuilding,
  designs,
  lowBayHighBuildingCSB,
  lowBayHighBuildingMSB,
  lowBayHighBuildingSubTypes,
  multiGableBuildingCSB,
  multiGableBuildingMSB,
  multiGableBuildingSubTypes,
  multiSpanBuilding,
} from "./EstimationSpecificationConstant";

const EstimationFilterModal = ({
  show,
  onHide,
  onApplyFilter,
  onClearFilter,
}) => {
  // which filter blocks are visible
  const [showStructureFilter, setShowStructureFilter] = useState(false);
  const [showDesignFilter, setShowDesignFilter] = useState(false);
  const [showGeometryFilter, setShowGeometryFilter] = useState(false);

  // main building filter object
  const [building, setBuilding] = useState({
    mainBuildingType: "",
    mainBuildingTypeCode: "",
    mainBuildingTypeDesc: "",
    subBuildingType: "",
    subBuildingTypeCode: "",
    subBuildingTypeDesc: "",
    buildingTypes: "",
    buildingTypesCode: "",
    buildingTypesDesc: "",
    buildingStructure: "", // Symmetric / Asymmetric
    buildingStructureCode: "",
    buildingStructureDesc: "",
    design: "",
    designCode: "",
    designDesc: "",
    designLoad: {
      deadLoad: "",
      liveLoad: "",
      solarLoad: "",
      windLoad: "",
    },
    designStandards: {
      codesApplicable: "",
      windLoadCalculation: "",
      windLoadApplication: "",
      seismicLoad: "",
    },
    // geometry will be filled in handleApplyFilter
    front: { meters: "" },
    back: { meters: "" },
    left: { meters: "" },
    right: { meters: "" },
    lswEaveHeight: "",
    rswEaveHeight: "",
    msNumber: "",
  });

  // geometry input state (always holds all 6 values)
  const [geometry, setGeometry] = useState({
    front: "",
    back: "",
    left: "",
    right: "",
    lswEaveHeight: "",
    rswEaveHeight: "",
  });

  const [subBuildingData, setSubBuildingData] = useState([]);
  const [buildingTypesData, setBuildingTypesData] = useState([]);
  const [getDropdownData, setDropdownData] = useState(null);

  const [isStructureOpen, setIsStructureOpen] = useState(true);
  const [isDesignOpen, setIsDesignOpen] = useState(true);
  const [isGeometryOpen, setIsGeometryOpen] = useState(true);

  const updateBuildingField = (name, value) => {
    setBuilding((prev) => ({ ...prev, [name]: value }));
  };

  const getSubBuildingData = (mainBuildingTypeCode) => {
    switch (mainBuildingTypeCode) {
      case "MGB":
        return multiGableBuildingSubTypes;
      case "LHB":
        return lowBayHighBuildingSubTypes;
      default:
        return [];
    }
  };

  const getBuildingTypesData = (mainBuildingTypeCode, subBuildingTypeCode) => {
    if (mainBuildingTypeCode === "MGB") {
      if (subBuildingTypeCode === "CSB") return multiGableBuildingCSB;
      if (subBuildingTypeCode === "MSB") return multiGableBuildingMSB;
    }
    if (mainBuildingTypeCode === "LHB") {
      if (subBuildingTypeCode === "CSB") return lowBayHighBuildingCSB;
      if (subBuildingTypeCode === "MSB") return lowBayHighBuildingMSB;
    }
    if (mainBuildingTypeCode === "CSB") return clearSpanBuilding;
    if (mainBuildingTypeCode === "MSB") return multiSpanBuilding;
    return [];
  };

  // dropdown selection
  useEffect(() => {
    if (!getDropdownData) return;

    const { textCode, name, code, description } = getDropdownData;

    // Type of building
    if (textCode?.includes("mainBuildingTypeCode")) {
      updateBuildingField("mainBuildingType", name);
      updateBuildingField("mainBuildingTypeCode", code);
      updateBuildingField("mainBuildingTypeDesc", description);

      updateBuildingField("subBuildingType", "");
      updateBuildingField("subBuildingTypeCode", "");
      updateBuildingField("subBuildingTypeDesc", "");
      updateBuildingField("buildingTypes", "");
      updateBuildingField("buildingTypesCode", "");
      updateBuildingField("buildingTypesDesc", "");

      if (code === "MLMB") {
        setSubBuildingData([]);
        setBuildingTypesData([]);
        updateBuildingField("buildingTypes", "Multi Level Mezzanine Building");
        updateBuildingField("buildingTypesCode", "MLMB");
        updateBuildingField(
          "buildingTypesDesc",
          "MULTI LEVEL MEZZANINE BUILDING"
        );
      } else if (code === "MGB" || code === "LHB") {
        setSubBuildingData(getSubBuildingData(code));
        setBuildingTypesData([]);
      } else if (code === "CSB" || code === "MSB") {
        setSubBuildingData([]);
        setBuildingTypesData(getBuildingTypesData(code, ""));
      }
    }

    // Sub building type
    else if (textCode?.includes("subBuildingTypeCode")) {
      updateBuildingField("subBuildingType", name);
      updateBuildingField("subBuildingTypeCode", code);
      updateBuildingField("subBuildingTypeDesc", description);

      const typesData = getBuildingTypesData(
        building.mainBuildingTypeCode,
        code
      );
      setBuildingTypesData(typesData);
      updateBuildingField("buildingTypes", "");
      updateBuildingField("buildingTypesCode", "");
      updateBuildingField("buildingTypesDesc", "");
    }

    // Building Types
    else if (textCode?.includes("buildingTypesCode")) {
      updateBuildingField("buildingTypes", name);
      updateBuildingField("buildingTypesCode", code);
      updateBuildingField("buildingTypesDesc", description);
    }

    // Building Structure
    else if (textCode?.includes("buildingStructureCode")) {
      updateBuildingField("buildingStructure", name);
      updateBuildingField("buildingStructureCode", code);
      updateBuildingField("buildingStructureDesc", description);
    }

    // Design (case type)
    else if (textCode?.includes("designCode")) {
      updateBuildingField("design", name);
      updateBuildingField("designCode", code);
      updateBuildingField("designDesc", description);
    }
  }, [getDropdownData, building.mainBuildingTypeCode]);

  // design load
  const handleDesignLoadChange = (field, value) => {
    setBuilding((prev) => ({
      ...prev,
      designLoad: { ...prev.designLoad, [field]: value },
    }));
  };

  // design standards
  const handleDesignStandardsChange = (field, value) => {
    setBuilding((prev) => ({
      ...prev,
      designStandards: { ...prev.designStandards, [field]: value },
    }));
  };

  // APPLY FILTER
  const handleApplyFilter = () => {
    let finalBuilding = { ...building };

    if (showGeometryFilter) {
      const frontVal = geometry.front;
      const backVal = geometry.back;
      const leftVal = geometry.left;
      const rightVal = geometry.right;

      if (showStructureFilter && building.buildingStructure === "Symmetric") {
        // user sees only front + left; backend must use:
        // back = front, right = left
        finalBuilding.front = { meters: frontVal };
        finalBuilding.back = { meters: frontVal || backVal };
        finalBuilding.left = { meters: leftVal };
        finalBuilding.right = { meters: leftVal || rightVal };
      } else {
        // either Asymmetric OR structure type not selected
        // use all 4 as they are entered
        finalBuilding.front = { meters: frontVal };
        finalBuilding.back = { meters: backVal };
        finalBuilding.left = { meters: leftVal };
        finalBuilding.right = { meters: rightVal };
      }

      finalBuilding.lswEaveHeight = geometry.lswEaveHeight;
      finalBuilding.rswEaveHeight = geometry.rswEaveHeight;
    }

    onApplyFilter({
      building: finalBuilding,
      useStructure: showStructureFilter,
      useDesign: showDesignFilter,
      useGeometry: showGeometryFilter,
    });
    onHide();
  };

  // CLEAR FILTER
  const handleClear = () => {
    setBuilding({
      mainBuildingType: "",
      mainBuildingTypeCode: "",
      mainBuildingTypeDesc: "",
      subBuildingType: "",
      subBuildingTypeCode: "",
      subBuildingTypeDesc: "",
      buildingTypes: "",
      buildingTypesCode: "",
      buildingTypesDesc: "",
      buildingStructure: "",
      buildingStructureCode: "",
      buildingStructureDesc: "",
      design: "",
      designCode: "",
      designDesc: "",
      designLoad: { deadLoad: "", liveLoad: "", solarLoad: "", windLoad: "" },
      designStandards: {
        codesApplicable: "",
        windLoadCalculation: "",
        windLoadApplication: "",
        seismicLoad: "",
      },
      front: { meters: "" },
      back: { meters: "" },
      left: { meters: "" },
      right: { meters: "" },
      lswEaveHeight: "",
      rswEaveHeight: "",
      msNumber: "",
    });
    setGeometry({
      front: "",
      back: "",
      left: "",
      right: "",
      lswEaveHeight: "",
      rswEaveHeight: "",
    });
    setShowStructureFilter(false);
    setShowDesignFilter(false);
    setShowGeometryFilter(false);

    onClearFilter && onClearFilter();
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} centered size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Estimation Details</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Col xs="12" className="d-flex align-items-center">
          {/* Structure Type */}
          <div className="form-check form-check-inline">
            <input
              type="checkbox"
              className="form-check-input"
              id="filterStructure"
              checked={showStructureFilter}
              onChange={(e) => setShowStructureFilter(e.target.checked)}
            />
            <label
              className="form-check-label fw-semibold"
              htmlFor="filterStructure"
            >
              Structure
            </label>
          </div>

          {/* Design */}
          <div className="form-check form-check-inline">
            <input
              type="checkbox"
              className="form-check-input"
              id="filterDesign"
              checked={showDesignFilter}
              onChange={(e) => setShowDesignFilter(e.target.checked)}
            />
            <label
              className="form-check-label fw-semibold"
              htmlFor="filterDesign"
            >
              Design
            </label>
          </div>

          {/* Geometry */}
          <div className="form-check form-check-inline">
            <input
              type="checkbox"
              className="form-check-input"
              id="filterGeometry"
              checked={showGeometryFilter}
              onChange={(e) => setShowGeometryFilter(e.target.checked)}
            />
            <label
              className="form-check-label fw-semibold"
              htmlFor="filterGeometry"
            >
              Geometry
            </label>
          </div>
        </Col>

        <Row>
          <div className="accordion mt-3 mb-1" id="accordionExample">
            {/* STRUCTURE FILTER */}
            {showStructureFilter && (
              <div className="accordion mt-3 mb-4" id="accordionExample">
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <Row className="accordion-button">
                      <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                        <i
                          className={`bi ${
                            building.isCardVisible11
                              ? "bi-chevron-right"
                              : "bi-chevron-down"
                          }`}
                          onClick={() => setIsStructureOpen((prev) => !prev)}
                        ></i>
                      </Col>
                      <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                        <span className="iconLogo">ST</span>
                      </Col>
                      <Col xs={4} md={4} lg={4}>
                        <span className="headerTitle">Structure</span>
                        <span className="subHeaderTitle"></span>
                      </Col>
                    </Row>
                  </h2>
                  <div
                    className={`accordion-collapse collapse show ${
                      isStructureOpen ? "visible" : ""
                    }`}
                  >
                    <div className="accordion-body pb-5">
                      <Row className="mb-3">
                        {/* Type of Building */}
                        <Col
                          xs={12}
                          md={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <ComboboxField
                            className="inputBox"
                            label="Type of Building"
                            placeholder=""
                            data={mainBuildingTypes}
                            name="mainBuildingType"
                            code="mainBuildingTypeCode-1"
                            description="description"
                            setValue={building.mainBuildingType || ""}
                            getvalue={setDropdownData}
                          />
                        </Col>

                        {/* Sub Building */}
                        {(building.mainBuildingTypeCode === "MGB" ||
                          building.mainBuildingTypeCode === "LHB") && (
                          <Col
                            xs={12}
                            md={4}
                            className="commTopButtonRightLeftPadding"
                          >
                            <ComboboxField
                              className="inputBox"
                              label="Sub Building"
                              placeholder=""
                              data={subBuildingData}
                              name="subBuildingType"
                              code="subBuildingTypeCode-1"
                              description="description"
                              setValue={building.subBuildingType || ""}
                              getvalue={setDropdownData}
                            />
                          </Col>
                        )}

                        {/* Building Types */}
                        {((building.mainBuildingTypeCode === "MGB" &&
                          building.subBuildingTypeCode) ||
                          (building.mainBuildingTypeCode === "LHB" &&
                            building.subBuildingTypeCode) ||
                          building.mainBuildingTypeCode === "CSB" ||
                          building.mainBuildingTypeCode === "MSB" ||
                          building.mainBuildingTypeCode === "MLMB") && (
                          <Col
                            xs={12}
                            md={4}
                            className="commTopButtonRightLeftPadding"
                          >
                            <ComboboxField
                              className="inputBox"
                              label="Building Types"
                              placeholder=""
                              data={buildingTypesData}
                              name="buildingTypes"
                              code="buildingTypesCode-1"
                              description="description"
                              setValue={building.buildingTypes || ""}
                              getvalue={setDropdownData}
                              disabled={
                                (building.mainBuildingTypeCode === "MGB" &&
                                  !building.subBuildingTypeCode) ||
                                (building.mainBuildingTypeCode === "LHB" &&
                                  !building.subBuildingTypeCode)
                              }
                            />
                          </Col>
                        )}

                        {/* MS Number for MSB */}
                        {(building.mainBuildingTypeCode === "MSB" ||
                          building.subBuildingTypeCode === "MSB") && (
                          <Col
                            xs={12}
                            md={4}
                            className="commTopButtonRightLeftPadding"
                          >
                            <InputField
                              className="inputBox"
                              label="MS Number"
                              name="msNumber"
                              value={building.msNumber || "MS-"}
                              onChange={(e) => {
                                const value = e.target.value;
                                const numberOnly = value.replace(/[^0-9]/g, "");
                                updateBuildingField(
                                  "msNumber",
                                  numberOnly ? `MS-${numberOnly}` : "MS-"
                                );
                              }}
                            />
                          </Col>
                        )}

                        {/* Building Structure */}
                        <Col
                          xs={12}
                          md={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <ComboboxField
                            className="inputBox"
                            data={buildingStrctures}
                            label="Building Structure"
                            name="buildingStructure"
                            code="buildingStructureCode-1"
                            description="description"
                            setValue={building.buildingStructure || ""}
                            getvalue={setDropdownData}
                          />
                        </Col>

                        {/* Design / case type */}
                        <Col
                          xs={12}
                          md={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <ComboboxField
                            className="inputBox"
                            data={designs}
                            label="Design"
                            name="design"
                            code="designCode-1"
                            description="description"
                            setValue={building.design || ""}
                            getvalue={setDropdownData}
                          />
                        </Col>
                      </Row>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* DESIGN FILTER */}
            {showDesignFilter && (
              <div className="accordion mt-3 mb-4" id="accordionExample">
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <Row className="accordion-button">
                      <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                        <i
                          className={`bi ${
                            building.isCardVisible11
                              ? "bi-chevron-right"
                              : "bi-chevron-down"
                          }`}
                          onClick={() => setIsDesignOpen((prev) => !prev)}
                        ></i>
                      </Col>
                      <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                        <span className="iconLogo">DI</span>
                      </Col>
                      <Col xs={4} md={4} lg={4}>
                        <span className="headerTitle">Design</span>
                        <span className="subHeaderTitle"></span>
                      </Col>
                    </Row>
                  </h2>
                  <div
                    className={`accordion-collapse collapse show ${
                      isDesignOpen ? "visible" : ""
                    }`}
                  >
                    <div className="accordion-body">
                      <Row className="mb-3 mt-1">
                        <Row className="p-3">
                          <Col xs={12}>
                            {/* Design Load */}
                            <Row className="mb-2">
                              <Col xs={12}>
                                <h5 className="text-primary">Design Load</h5>
                              </Col>
                              <Col
                                xs={12}
                                md={3}
                                className="commTopButtonRightLeftPadding"
                              >
                                <InputField
                                  label="Dead Load (kn/Sq.m)"
                                  className="inputBox"
                                  name="deadLoad"
                                  value={building.designLoad.deadLoad}
                                  onChange={(e) =>
                                    handleDesignLoadChange(
                                      "deadLoad",
                                      e.target.value
                                    )
                                  }
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={3}
                                className="commTopButtonRightLeftPadding"
                              >
                                <InputField
                                  label="Live Load (kn/Sq.m)"
                                  className="inputBox"
                                  name="liveLoad"
                                  value={building.designLoad.liveLoad}
                                  onChange={(e) =>
                                    handleDesignLoadChange(
                                      "liveLoad",
                                      e.target.value
                                    )
                                  }
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={3}
                                className="commTopButtonRightLeftPadding"
                              >
                                <InputField
                                  label="Solar Load (kn/Sq.m)"
                                  className="inputBox"
                                  name="solarLoad"
                                  value={building.designLoad.solarLoad}
                                  onChange={(e) =>
                                    handleDesignLoadChange(
                                      "solarLoad",
                                      e.target.value
                                    )
                                  }
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={3}
                                className="commTopButtonRightLeftPadding"
                              >
                                <InputField
                                  label="Wind Speed (kmph)"
                                  className="inputBox"
                                  name="windLoad"
                                  value={building.designLoad.windLoad}
                                  onChange={(e) =>
                                    handleDesignLoadChange(
                                      "windLoad",
                                      e.target.value
                                    )
                                  }
                                />
                              </Col>
                            </Row>

                            {/* Design Standards */}
                            <Row className="mb-2 mt-3">
                              <Col xs={12}>
                                <h5 className="mb-2 text-primary">
                                  Design Standards
                                </h5>
                              </Col>
                              <Col
                                xs={12}
                                md={3}
                                className="commTopButtonRightLeftPadding"
                              >
                                <InputField
                                  label="Codes Applicable"
                                  className="inputBox"
                                  name="codesApplicable"
                                  value={
                                    building.designStandards.codesApplicable
                                  }
                                  onChange={(e) =>
                                    handleDesignStandardsChange(
                                      "codesApplicable",
                                      e.target.value
                                    )
                                  }
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={3}
                                className="commTopButtonRightLeftPadding"
                              >
                                <InputField
                                  label="Wind Pressure Calculation"
                                  className="inputBox"
                                  name="windLoadCalculation"
                                  value={
                                    building.designStandards.windLoadCalculation
                                  }
                                  onChange={(e) =>
                                    handleDesignStandardsChange(
                                      "windLoadCalculation",
                                      e.target.value
                                    )
                                  }
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={3}
                                className="commTopButtonRightLeftPadding"
                              >
                                <InputField
                                  label="Wind Load Application"
                                  className="inputBox"
                                  name="windLoadApplication"
                                  value={
                                    building.designStandards.windLoadApplication
                                  }
                                  onChange={(e) =>
                                    handleDesignStandardsChange(
                                      "windLoadApplication",
                                      e.target.value
                                    )
                                  }
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={3}
                                className="commTopButtonRightLeftPadding"
                              >
                                <InputField
                                  label="Seismic Zone"
                                  className="inputBox"
                                  name="seismicLoad"
                                  value={building.designStandards.seismicLoad}
                                  onChange={(e) =>
                                    handleDesignStandardsChange(
                                      "seismicLoad",
                                      e.target.value
                                    )
                                  }
                                />
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      </Row>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* GEOMETRY FILTER */}
            {showGeometryFilter && (
              <div className="accordion mt-3 mb-4" id="accordionExample">
                <div className="accordion-item mb-3">
                  <h2 className="accordion-header">
                    <Row className="accordion-button">
                      <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                        <i
                          className={`bi ${
                            building.isCardVisible11
                              ? "bi-chevron-right"
                              : "bi-chevron-down"
                          }`}
                          onClick={() => setIsGeometryOpen((prev) => !prev)}
                        ></i>
                      </Col>
                      <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                        <span className="iconLogo">GI</span>
                      </Col>
                      <Col xs={4} md={4} lg={4}>
                        <span className="headerTitle">
                          Geometric
                        </span>
                        <span className="subHeaderTitle"></span>
                      </Col>
                    </Row>
                  </h2>
                  <div
                    className={`accordion-collapse collapse show ${
                      isGeometryOpen ? "visible" : ""
                    }`}
                  >
                    <div className="accordion-body">
                      <Row className="mb-3 mt-1">
                        {/* FRONT: always visible */}
                        <Col
                          xs={12}
                          md={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <InputField
                            className="inputBox"
                            label="Front End Wall Length (m)"
                            name="geomFront"
                            value={geometry.front}
                            onChange={(e) =>
                              setGeometry((prev) => ({
                                ...prev,
                                front: e.target.value,
                              }))
                            }
                          />
                        </Col>

                        {/* BACK:
                  - Visible if Structure filter is OFF
                  - OR Structure = Asymmetric
              */}
                        {(!showStructureFilter ||
                          building.buildingStructure === "Asymmetric") && (
                          <Col
                            xs={12}
                            md={4}
                            className="commTopButtonRightLeftPadding"
                          >
                            <InputField
                              className="inputBox"
                              label="Back End Wall Length (m)"
                              name="geomBack"
                              value={geometry.back}
                              onChange={(e) =>
                                setGeometry((prev) => ({
                                  ...prev,
                                  back: e.target.value,
                                }))
                              }
                            />
                          </Col>
                        )}

                        {/* LEFT: always visible */}
                        <Col
                          xs={12}
                          md={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <InputField
                            className="inputBox"
                            label="Left Side Wall Length (m)"
                            name="geomLeft"
                            value={geometry.left}
                            onChange={(e) =>
                              setGeometry((prev) => ({
                                ...prev,
                                left: e.target.value,
                              }))
                            }
                          />
                        </Col>

                        {/* RIGHT:
                  - Visible if Structure filter is OFF
                  - OR Structure = Asymmetric
              */}
                        {(!showStructureFilter ||
                          building.buildingStructure === "Asymmetric") && (
                          <Col
                            xs={12}
                            md={4}
                            className="commTopButtonRightLeftPadding mt-2"
                          >
                            <InputField
                              className="inputBox"
                              label="Right Side Wall Length (m)"
                              name="geomRight"
                              value={geometry.right}
                              onChange={(e) =>
                                setGeometry((prev) => ({
                                  ...prev,
                                  right: e.target.value,
                                }))
                              }
                            />
                          </Col>
                        )}

                        {/* EAVE HEIGHTS: always visible */}
                        <Col
                          xs={12}
                          md={4}
                          className="commTopButtonRightLeftPadding mt-2"
                        >
                          <InputField
                            className="inputBox"
                            label="LSW Eave Height (m)"
                            name="geomLswEave"
                            value={geometry.lswEaveHeight}
                            onChange={(e) =>
                              setGeometry((prev) => ({
                                ...prev,
                                lswEaveHeight: e.target.value,
                              }))
                            }
                          />
                        </Col>

                        <Col
                          xs={12}
                          md={4}
                          className="commTopButtonRightLeftPadding mt-2"
                        >
                          <InputField
                            className="inputBox"
                            label="RSW Eave Height (m)"
                            name="geomRswEave"
                            value={geometry.rswEaveHeight}
                            onChange={(e) =>
                              setGeometry((prev) => ({
                                ...prev,
                                rswEaveHeight: e.target.value,
                              }))
                            }
                          />
                        </Col>
                      </Row>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        <Button variant="outline-secondary" onClick={handleClear}>
          Clear Filter
        </Button>
        <Button
          variant="primary"
          onClick={handleApplyFilter}
          className="bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500"
        >
          Apply Filter
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EstimationFilterModal;
